import { computed, toValue } from 'vue';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/vue-query';
import type {
  MaterialRequestForm,
  MaterialRequestLabel,
} from '@cutr/constants/material-request';
import {
  createMaterialRequest as createMaterialRequestRequest,
  deleteMaterialRequest as deleteMaterialRequestRequest,
  getMaterialRequests,
  updateMaterialRequest as updateMaterialRequestRequest,
  updateMaterialRequestLabel as updateMaterialRequestLabelRequest,
  type MaterialRequestRow,
} from '@/api/materialRequests';
import { materialRequestKeys } from './keys';
import { useInvalidateParts } from './parts';
import { patchRow, removeRow } from './shared/listUpdaters';
import { useInvalidateScoped } from './shared/useInvalidateScoped';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId, QueryFlags, WithVariant } from './shared/types';

export function materialRequestListOptions(variantId: string) {
  return queryOptions({
    queryKey: materialRequestKeys.list(variantId),
    queryFn: ({ signal }) => getMaterialRequests(variantId, signal),
  });
}

export function useMaterialRequestsQuery(
  variantId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(variantId, materialRequestListOptions, flags);

  return {
    query,
    materialRequests: computed(() => query.data.value ?? []),
  };
}

export function useInvalidateMaterialRequests() {
  return useInvalidateScoped(materialRequestKeys.list);
}

export function useMaterialRequestMutations(variantId: MaybeId) {
  const queryClient = useQueryClient();
  const orderVariantId = () => toValue(variantId) ?? '';

  const getList = (variantId: string) =>
    queryClient.getQueryData<MaterialRequestRow[]>(
      materialRequestKeys.list(variantId)
    );

  const setList = (
    variantId: string,
    updater: (
      list: MaterialRequestRow[] | undefined
    ) => MaterialRequestRow[] | undefined
  ) =>
    queryClient.setQueryData<MaterialRequestRow[]>(
      materialRequestKeys.list(variantId),
      updater
    );

  const invalidateList = (variantId: string) =>
    queryClient.invalidateQueries({
      queryKey: materialRequestKeys.list(variantId),
    });

  const invalidateParts = useInvalidateParts();

  /** The list is ordered `created_at DESC`, so a new request belongs first. */
  const prepend = (request: MaterialRequestRow) => {
    const current = getList(request.orderVariantId);
    if (!current) return invalidateList(request.orderVariantId);
    if (current.some((entry) => entry.id === request.id)) return;

    return setList(request.orderVariantId, () => [request, ...current]);
  };

  const replace = (request: MaterialRequestRow) => {
    const current = getList(request.orderVariantId);
    if (!current?.some((entry) => entry.id === request.id)) {
      return invalidateList(request.orderVariantId);
    }

    return setList(request.orderVariantId, () =>
      current.map((entry) => (entry.id === request.id ? request : entry))
    );
  };

  const applyOptimistic = async (
    variantId: string,
    updater: (
      list: MaterialRequestRow[] | undefined
    ) => MaterialRequestRow[] | undefined
  ) => {
    await queryClient.cancelQueries({
      queryKey: materialRequestKeys.list(variantId),
    });
    setList(variantId, updater);
  };

  const create = useMutation({
    mutationFn: createMaterialRequestRequest,
    onSuccess(request, variables) {
      prepend(request);
      if (variables.partIds?.length) invalidateParts(request.orderVariantId);
    },
  });

  const update = useMutation({
    mutationFn: updateMaterialRequestRequest,
    onSuccess(request, variables) {
      replace(request);
      if (variables.partIds) invalidateParts(request.orderVariantId);
    },
  });

  const remove = useMutation({
    mutationFn: ({ id }: WithVariant<{ id: string }>) =>
      deleteMaterialRequestRequest(id),
    onMutate: ({ id, orderVariantId }) =>
      applyOptimistic(orderVariantId, removeRow(id)),
    onSuccess: (_data, { orderVariantId }) => invalidateParts(orderVariantId),
    onError: (_error, { orderVariantId }) => invalidateList(orderVariantId),
  });

  const updateLabel = useMutation({
    mutationFn: ({
      id,
      data,
    }: WithVariant<{ id: string; data: MaterialRequestLabel }>) =>
      updateMaterialRequestLabelRequest(id, data),
    onMutate: ({ id, orderVariantId, data }) =>
      applyOptimistic(
        orderVariantId,
        patchRow<MaterialRequestRow>({ id, ...data })
      ),
    onError: (_error, { orderVariantId }) => invalidateList(orderVariantId),
  });

  const done = (result: Promise<unknown>) => result.then(() => undefined);
  const withVariant = <T>(data: T) => ({
    ...data,
    orderVariantId: orderVariantId(),
  });

  return {
    createMaterialRequest: (data: Omit<MaterialRequestForm, 'id'>) =>
      create.mutateAsync(withVariant(data)),
    updateMaterialRequest: (data: MaterialRequestForm) =>
      update.mutateAsync(data),
    deleteMaterialRequest: (id: string) =>
      done(remove.mutateAsync(withVariant({ id }))),
    updateMaterialRequestLabel: (id: string, data: MaterialRequestLabel) =>
      done(updateLabel.mutateAsync(withVariant({ id, data }))),
  };
}
